import type { FeatureModule, FeatureState } from '../types';

/**
 * Text-Lupe: zeigt den Text unter dem Mauszeiger vergrößert in einer fixierten
 * Lese-Leiste am oberen Rand. Bewusst KEIN grafischer Pixel-Zoom (bräuchte
 * html2canvas, ~50 KB + langsam und sprengt das Bundle-Budget); der reale
 * Mehrwert bei Sehschwäche ist „beliebigen Text groß lesen", und genau das
 * leistet die Leiste robust auf jeder Seite.
 */
export default function createMagnifierModule(): FeatureModule {
  let enabled = false;
  let bar: HTMLDivElement | null = null;
  let lastTarget: HTMLElement | null = null;
  const BAR_ID = 'accessify-magnifier-bar';
  const STORAGE_KEY = 'accessify-magnifier';

  function isOwnUi(el: HTMLElement | null): boolean {
    if (!el) return false;
    if (bar && (el === bar || bar.contains(el))) return true;
    const root =
      document.getElementById('accessify-root') ||
      document.querySelector('[data-accessify-root="true"]');
    return !!root && (root === el || root.contains(el as Node));
  }

  // Vom getroffenen Knoten zum nächsten sinnvollen Text-Container hochlaufen.
  function textUnder(el: HTMLElement): string {
    let node: HTMLElement | null = el;
    let depth = 0;
    while (node && node !== document.body && depth < 6) {
      const txt = (node.innerText || node.textContent || '').trim();
      if (txt && txt.length <= 600) return txt;
      node = node.parentElement;
      depth++;
    }
    return '';
  }

  function onMove(e: MouseEvent) {
    if (!enabled || !bar) return;
    const target = e.target as HTMLElement;
    if (isOwnUi(target)) {
      bar.style.opacity = '0';
      return;
    }
    if (target === lastTarget) return;
    lastTarget = target;
    const text = textUnder(target);
    if (text) {
      bar.textContent = text;
      bar.style.opacity = '1';
    } else {
      bar.style.opacity = '0';
    }
  }

  function createBar(): HTMLDivElement {
    const el = document.createElement('div');
    el.id = BAR_ID;
    el.setAttribute('aria-hidden', 'true');
    Object.assign(el.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      right: '0',
      zIndex: '2147483645',
      boxSizing: 'border-box',
      maxHeight: '38vh',
      overflow: 'auto',
      padding: '18px 24px',
      margin: '0',
      background: '#101011',
      color: '#ffffff',
      font: '600 30px/1.45 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
      letterSpacing: '0.01em',
      boxShadow: '0 6px 24px rgba(0,0,0,.35)',
      opacity: '0',
      transition: 'opacity .12s ease',
      pointerEvents: 'none',
    });
    return el;
  }

  function activate() {
    enabled = true;
    if (!bar) {
      bar = createBar();
      document.documentElement.appendChild(bar);
    }
    document.addEventListener('mousemove', onMove, true);
    localStorage.setItem(STORAGE_KEY, 'true');
  }

  function deactivate() {
    enabled = false;
    document.removeEventListener('mousemove', onMove, true);
    bar?.remove();
    bar = null;
    lastTarget = null;
    localStorage.removeItem(STORAGE_KEY);
  }

  return {
    id: 'magnifier',
    name: () => 'Text magnifier',
    description: 'Show the text under your pointer enlarged in a reading bar',
    icon: 'magnifier',
    category: 'visual',
    activate,
    deactivate,
    getState: (): FeatureState => ({ id: 'magnifier', enabled }),
    setState: (state: { enabled: boolean }) => {
      if (state.enabled) activate();
      else deactivate();
    },
  };
}
